import type { AnchorSide, Cardinality, ConnectorEndpoint, DiagramDocument, DiagramEdge, DiagramNode, DiagramPage, EntityField, LinePattern, NodeStyle, NodeType, TextAlign, UmlRelationshipType } from './types';
import { SHAPE_DEFINITIONS } from './shapeRegistry';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const NODE_TYPES: NodeType[] = SHAPE_DEFINITIONS.map((shape) => shape.type);
const ANCHORS: AnchorSide[] = ['auto','topLeft','top','topRight','rightTop','right','rightBottom','bottomRight','bottom','bottomLeft','leftBottom','left','leftTop'];
const ENDPOINTS: ConnectorEndpoint[] = ['none','arrow','circle','diamond','square'];
const PATTERNS: LinePattern[] = ['solid', 'dashed'];
const TEXT_ALIGNMENTS: TextAlign[] = ['left', 'center', 'right'];
const CARDINALITIES: Cardinality[] = ['1', '0..1', 'N', '1..N', '0..N'];
const UML_RELATIONSHIPS: UmlRelationshipType[] = ['association', 'dependency', 'inheritance', 'aggregation', 'composition', 'include', 'extend'];
const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/;

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function boundedNumber(value: unknown, min: number, max: number): boolean {
  return finite(value) && value >= min && value <= max;
}

function validId(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 1 && value.length <= 160 && /^[A-Za-z0-9._:-]+$/.test(value);
}

function validIsoDate(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 100 && !Number.isNaN(Date.parse(value));
}

function validStyle(style: unknown, path: string, errors: string[]): style is NodeStyle {
  if (!style || typeof style !== 'object' || Array.isArray(style)) {
    errors.push(`${path}: estilo inválido.`);
    return false;
  }
  const value = style as Partial<NodeStyle>;
  if (typeof value.fill !== 'string' || !HEX_COLOR.test(value.fill)) errors.push(`${path}.fill inválido.`);
  if (typeof value.stroke !== 'string' || !HEX_COLOR.test(value.stroke)) errors.push(`${path}.stroke inválido.`);
  if (typeof value.textColor !== 'string' || !HEX_COLOR.test(value.textColor)) errors.push(`${path}.textColor inválido.`);
  if (!boundedNumber(value.strokeWidth, 0.5, 20)) errors.push(`${path}.strokeWidth inválido.`);
  if (!boundedNumber(value.fontSize, 6, 200)) errors.push(`${path}.fontSize inválido.`);
  if (!boundedNumber(value.radius, 0, 600)) errors.push(`${path}.radius inválido.`);
  if (value.opacity !== undefined && !boundedNumber(value.opacity, 0.05, 1)) errors.push(`${path}.opacity inválido.`);
  if (value.linePattern !== undefined && !PATTERNS.includes(value.linePattern as LinePattern)) errors.push(`${path}.linePattern inválido.`);
  if (value.textAlign !== undefined && !TEXT_ALIGNMENTS.includes(value.textAlign as TextAlign)) errors.push(`${path}.textAlign inválido.`);
  if (value.fontWeight !== undefined && !boundedNumber(value.fontWeight, 100, 900)) errors.push(`${path}.fontWeight inválido.`);
  return true;
}

function validData(data: unknown, path: string, errors: string[]): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    errors.push(`${path}: dados inválidos.`);
    return false;
  }
  const entries = Object.entries(data as Record<string, unknown>);
  if (entries.length > 200) errors.push(`${path}: quantidade máxima de campos excedida.`);
  for (const [key, value] of entries) {
    if (!key || key.length > 100 || ['__proto__', 'prototype', 'constructor'].includes(key)) errors.push(`${path}: nome de campo inválido.`);
    if (typeof value === 'string' && value.length > 10_000) errors.push(`${path}.${key}: texto muito grande.`);
    if (!(value === null || ['string', 'number', 'boolean'].includes(typeof value))) errors.push(`${path}.${key}: tipo de valor não permitido.`);
    if (typeof value === 'number' && !Number.isFinite(value)) errors.push(`${path}.${key}: número inválido.`);
  }
  return true;
}

function validEntityFields(fields: unknown, path: string, errors: string[]): fields is EntityField[] {
  if (!Array.isArray(fields)) { errors.push(`${path}: campos ERD inválidos.`); return false; }
  if (fields.length > 500) errors.push(`${path}: limite de 500 campos excedido.`);
  const ids = new Set<string>();
  fields.forEach((raw, index) => {
    const itemPath = `${path}[${index}]`;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) { errors.push(`${itemPath}: campo inválido.`); return; }
    const field = raw as Partial<EntityField>;
    if (!validId(field.id)) errors.push(`${itemPath}.id inválido.`); else if (ids.has(field.id)) errors.push(`${itemPath}.id duplicado.`); else ids.add(field.id);
    if (typeof field.name !== 'string' || field.name.length < 1 || field.name.length > 300) errors.push(`${itemPath}.name inválido.`);
    if (typeof field.dataType !== 'string' || field.dataType.length < 1 || field.dataType.length > 200) errors.push(`${itemPath}.dataType inválido.`);
    if (typeof field.primaryKey !== 'boolean' || typeof field.foreignKey !== 'boolean' || typeof field.nullable !== 'boolean') errors.push(`${itemPath}: flags inválidas.`);
  });
  return true;
}

function validNode(node: unknown, path: string, errors: string[]): node is DiagramNode {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    errors.push(`${path}: objeto inválido.`);
    return false;
  }
  const value = node as Partial<DiagramNode>;
  if (!validId(value.id)) errors.push(`${path}.id inválido.`);
  if (!NODE_TYPES.includes(value.type as NodeType)) errors.push(`${path}.type inválido.`);
  if (!boundedNumber(value.x, -1_000_000, 1_000_000) || !boundedNumber(value.y, -1_000_000, 1_000_000)) errors.push(`${path}: posição inválida.`);
  if (!boundedNumber(value.width, 1, 100_000) || !boundedNumber(value.height, 1, 100_000)) errors.push(`${path}: dimensões inválidas.`);
  if (!boundedNumber(value.rotation, -360_000, 360_000)) errors.push(`${path}.rotation inválido.`);
  if (typeof value.text !== 'string' || value.text.length > 50_000) errors.push(`${path}.text inválido.`);
  validStyle(value.style, `${path}.style`, errors);
  validData(value.data, `${path}.data`, errors);
  if (value.locked !== undefined && typeof value.locked !== 'boolean') errors.push(`${path}.locked inválido.`);
  if (value.hidden !== undefined && typeof value.hidden !== 'boolean') errors.push(`${path}.hidden inválido.`);
  if (value.groupId !== undefined && value.groupId !== null && !validId(value.groupId)) errors.push(`${path}.groupId inválido.`);
  if (value.entityFields !== undefined) validEntityFields(value.entityFields, `${path}.entityFields`, errors);
  for (const [key, list] of [['umlAttributes', value.umlAttributes], ['umlMethods', value.umlMethods]] as const) {
    if (list !== undefined) {
      if (!Array.isArray(list) || list.length > 500 || list.some((item) => typeof item !== 'string' || item.length > 1000)) errors.push(`${path}.${key} inválido.`);
    }
  }
  return true;
}

function validEdge(edge: unknown, nodeIds: Set<string>, path: string, errors: string[]): edge is DiagramEdge {
  if (!edge || typeof edge !== 'object' || Array.isArray(edge)) {
    errors.push(`${path}: conexão inválida.`);
    return false;
  }
  const value = edge as Partial<DiagramEdge>;
  if (!validId(value.id)) errors.push(`${path}.id inválido.`);
  if (!validId(value.sourceId) || !nodeIds.has(value.sourceId)) errors.push(`${path}.sourceId aponta para objeto inexistente.`);
  if (!validId(value.targetId) || !nodeIds.has(value.targetId)) errors.push(`${path}.targetId aponta para objeto inexistente.`);
  if (value.sourceId === value.targetId) errors.push(`${path}: auto-conexão ainda não é suportada.`);
  if (typeof value.label !== 'string' || value.label.length > 5_000) errors.push(`${path}.label inválido.`);
  if (!['straight', 'orthogonal'].includes(String(value.style))) errors.push(`${path}.style inválido.`);
  if (typeof value.stroke !== 'string' || !HEX_COLOR.test(value.stroke)) errors.push(`${path}.stroke inválido.`);
  if (!boundedNumber(value.strokeWidth, 0.5, 20)) errors.push(`${path}.strokeWidth inválido.`);
  if (typeof value.arrowEnd !== 'boolean') errors.push(`${path}.arrowEnd inválido.`);
  if (value.sourceAnchor !== undefined && !ANCHORS.includes(value.sourceAnchor)) errors.push(`${path}.sourceAnchor inválido.`);
  if (value.targetAnchor !== undefined && !ANCHORS.includes(value.targetAnchor)) errors.push(`${path}.targetAnchor inválido.`);
  if (value.linePattern !== undefined && !PATTERNS.includes(value.linePattern)) errors.push(`${path}.linePattern inválido.`);
  if (value.arrowStart !== undefined && typeof value.arrowStart !== 'boolean') errors.push(`${path}.arrowStart inválido.`);
  if (value.startMarker !== undefined && !ENDPOINTS.includes(value.startMarker as ConnectorEndpoint)) errors.push(`${path}.startMarker inválido.`);
  if (value.endMarker !== undefined && !ENDPOINTS.includes(value.endMarker as ConnectorEndpoint)) errors.push(`${path}.endMarker inválido.`);
  if (value.routingMode !== undefined && !['auto', 'manual'].includes(String(value.routingMode))) errors.push(`${path}.routingMode inválido.`);
  if (value.sourceCardinality !== undefined && !CARDINALITIES.includes(value.sourceCardinality)) errors.push(`${path}.sourceCardinality inválida.`);
  if (value.targetCardinality !== undefined && !CARDINALITIES.includes(value.targetCardinality)) errors.push(`${path}.targetCardinality inválida.`);
  if (value.relationshipType !== undefined && !UML_RELATIONSHIPS.includes(value.relationshipType)) errors.push(`${path}.relationshipType inválido.`);
  if (value.bundleId !== undefined && value.bundleId !== null && (typeof value.bundleId !== 'string' || value.bundleId.length < 1 || value.bundleId.length > 160)) errors.push(`${path}.bundleId inválido.`);
  if (value.waypoints !== undefined) {
    if (!Array.isArray(value.waypoints) || value.waypoints.length > 200) errors.push(`${path}.waypoints inválido.`);
    else value.waypoints.forEach((point, pointIndex) => {
      if (!point || typeof point !== 'object' || !boundedNumber((point as { x?: unknown }).x, -1_000_000, 1_000_000) || !boundedNumber((point as { y?: unknown }).y, -1_000_000, 1_000_000)) errors.push(`${path}.waypoints[${pointIndex}] inválido.`);
    });
  }
  return true;
}

function validatePage(page: unknown, index: number, errors: string[]): page is DiagramPage {
  const path = `pages[${index}]`;
  if (!page || typeof page !== 'object' || Array.isArray(page)) {
    errors.push(`${path}: página inválida.`);
    return false;
  }
  const value = page as Partial<DiagramPage>;
  if (!validId(value.id)) errors.push(`${path}.id inválido.`);
  if (typeof value.name !== 'string' || value.name.length > 500) errors.push(`${path}.name inválido.`);
  if (!Array.isArray(value.nodes)) errors.push(`${path}.nodes precisa ser uma lista.`);
  if (!Array.isArray(value.edges)) errors.push(`${path}.edges precisa ser uma lista.`);
  if (!Array.isArray(value.nodes) || !Array.isArray(value.edges)) return false;
  if (value.nodes.length > 100_000) errors.push(`${path}: quantidade máxima de objetos excedida.`);
  if (value.edges.length > 200_000) errors.push(`${path}: quantidade máxima de conexões excedida.`);

  value.nodes.forEach((node, nodeIndex) => validNode(node, `${path}.nodes[${nodeIndex}]`, errors));
  const nodeIdsList = value.nodes.map((node) => (node as Partial<DiagramNode>).id).filter((id): id is string => typeof id === 'string');
  const nodeIds = new Set(nodeIdsList);
  if (nodeIds.size !== nodeIdsList.length) errors.push(`${path}: IDs de objetos duplicados.`);

  value.edges.forEach((edge, edgeIndex) => validEdge(edge, nodeIds, `${path}.edges[${edgeIndex}]`, errors));
  const edgeIds = value.edges.map((edge) => (edge as Partial<DiagramEdge>).id).filter((id): id is string => typeof id === 'string');
  if (new Set(edgeIds).size !== edgeIds.length) errors.push(`${path}: IDs de conexões duplicados.`);
  return true;
}

export function validateDocument(value: unknown): ValidationResult {
  const errors: string[] = [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { valid: false, errors: ['Documento inválido.'] };
  const document = value as Partial<DiagramDocument>;
  if (document.schemaVersion !== 1) errors.push('schemaVersion não suportada.');
  if (!validId(document.id)) errors.push('id do documento inválido.');
  if (typeof document.title !== 'string' || document.title.length > 1_000) errors.push('title inválido.');
  if (!validIsoDate(document.createdAt)) errors.push('createdAt inválido.');
  if (!validIsoDate(document.updatedAt)) errors.push('updatedAt inválido.');
  if (!Array.isArray(document.pages) || document.pages.length === 0) errors.push('O documento precisa ter ao menos uma página.');
  if (Array.isArray(document.pages) && document.pages.length > 1_000) errors.push('Quantidade máxima de páginas excedida.');

  if (Array.isArray(document.pages)) {
    document.pages.forEach((page, index) => validatePage(page, index, errors));
    const pageIdsList = document.pages.map((page) => (page as Partial<DiagramPage>).id).filter((id): id is string => typeof id === 'string');
    const pageIds = new Set(pageIdsList);
    if (pageIds.size !== pageIdsList.length) errors.push('IDs de páginas duplicados.');
    if (!validId(document.activePageId) || !pageIds.has(document.activePageId)) errors.push('activePageId inválido.');
  }

  if (!document.metadata || typeof document.metadata !== 'object') errors.push('metadata inválido.');
  else {
    if (document.metadata.app !== 'OWDEX Draw') errors.push('metadata.app inválido.');
    if (typeof document.metadata.version !== 'string' || document.metadata.version.length > 100) errors.push('metadata.version inválido.');
  }

  return { valid: errors.length === 0, errors: errors.slice(0, 100) };
}
